'use client';

import { useTranslations } from 'next-intl';
import { useEffect } from 'react';

import { Button } from '@commons/components/Button';
import Drawer from '@commons/components/Drawer';
import Separator from '@commons/components/Separator';
import Text from '@commons/components/Text';
import { useRouter } from '@commons/i18n/navigation';

// Type definition for the DrawerSlotError component props.

type Props = {
  error: Error & { digest?: string };
  reset: () => void;
};

/**
 * DrawerSlotError component.
 *
 * Must be a client component as it requires side effects.
 *
 * @see https://nextjs.org/docs/app/building-your-application/routing/error-handling#how-errorjs-works
 */

const DrawerSlotError = ({ error }: Props) => {
  const t = useTranslations('pages.drawerSlotError');
  const { back } = useRouter();

  // Log the error to an error reporting service.
  useEffect(() => {
    console.error(error);
  }, [error]);

  const handleClick = () => {
    back();
  };

  return (
    <>
      <Separator dash color="orange-2" marginBottom="md" />
      <Drawer.Title asChild>
        <Text as="h1" level="h2" transform="uppercase">
          {t('title')}
        </Text>
      </Drawer.Title>
      <Drawer.Description asChild>
        <Text>{t('description.unavailable')}</Text>
      </Drawer.Description>
      <Text marginBottom="md">{t('description.resolve')}</Text>
      <Button variant="primary" onClick={handleClick}>
        {t('button')}
      </Button>
    </>
  );
};

export default DrawerSlotError;
